import React from "react";
import { Box, Typography, Table, Sheet, Button, Stack } from "@mui/joy";
import { AccessRequest } from "../../common/types";
import Loading from "../Loading";

interface AccessRequestsListProps {
  accessRequests: AccessRequest[];
  loading: boolean;
  approvingRequest: string | null;
  rejectingRequest: string | null;
  onApprove: (request: AccessRequest) => void;
  onReject: (request: AccessRequest) => void;
}

const AccessRequestsList: React.FC<AccessRequestsListProps> = ({
  accessRequests,
  loading,
  approvingRequest,
  rejectingRequest,
  onApprove,
  onReject,
}) => {
  if (loading) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Loading />
      </Box>
    );
  }

  if (accessRequests.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <Typography level="body-sm" color="neutral">
          No pending access requests.
        </Typography>
      </Box>
    );
  }

  return (
    <Sheet variant="outlined" sx={{ borderRadius: "sm" }}>
      <Table stickyHeader>
        <thead>
          <tr>
            <th
              style={{
                minWidth: "180px",
                width: "25%",
                backgroundColor: "var(--joy-palette-background-surface)",
              }}
            >
              Email
            </th>
            <th
              style={{
                width: "35%",
                backgroundColor: "var(--joy-palette-background-surface)",
              }}
            >
              Message
            </th>
            <th
              style={{
                width: "120px",
                backgroundColor: "var(--joy-palette-background-surface)",
              }}
            >
              Requested
            </th>
            <th
              style={{
                width: "200px",
                backgroundColor: "var(--joy-palette-background-surface)",
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {accessRequests.map((request) => (
            <tr key={request.user_id}>
              <td>{request.email}</td>
              <td>
                {request.access_request_message ? (
                  <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
                    {request.access_request_message}
                  </Typography>
                ) : (
                  <Typography
                    level="body-sm"
                    color="neutral"
                    fontStyle="italic"
                  >
                    No message
                  </Typography>
                )}
              </td>
              <td>
                <Typography level="body-sm" sx={{ whiteSpace: "nowrap" }}>
                  {new Date(request.access_request_date).toLocaleDateString()}
                </Typography>
              </td>
              <td>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "nowrap" }}>
                  <Button
                    variant="solid"
                    color="success"
                    size="sm"
                    onClick={() => onApprove(request)}
                    disabled={
                      approvingRequest === request.user_id ||
                      rejectingRequest === request.user_id
                    }
                    loading={approvingRequest === request.user_id}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="danger"
                    size="sm"
                    onClick={() => onReject(request)}
                    disabled={
                      approvingRequest === request.user_id ||
                      rejectingRequest === request.user_id
                    }
                  >
                    Reject
                  </Button>
                </Stack>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

export default AccessRequestsList;
